import { Link, useParams } from "react-router-dom";
import { usePageMeta } from "../../hooks/usePageMeta";
import { ARTICLES } from "../../mocks/articles";
import { getCategory } from "../../mocks/site";
import { formatCount } from "../../utils/format";
import Breadcrumb from "../../components/feature/Breadcrumb";
import { ArticleCard } from "../../components/feature/ArticleCard";

export default function NewsDetail() {
  const { id = "" } = useParams();
  const article = ARTICLES.find((a) => a.id === id);

  usePageMeta({
    title: article ? `${article.title} · 吃瓜17最新爆料` : "资讯未找到 · 吃瓜17",
    description: article ? article.summary : "您要查看的内容不存在或已被移除。",
    keywords: article ? ["吃瓜17", article.title].join(",") : "吃瓜17",
    path: `/news/${id}`,
    ogType: "article",
    schema: article
      ? [
          {
            "@context": "https://schema.org",
            "@type": "NewsArticle",
            headline: article.title,
            description: article.summary,
            image: [article.cover],
            datePublished: "2026-09-06",
            inLanguage: "zh-CN",
            author: { "@type": "Person", name: article.author },
            publisher: { "@type": "Organization", name: "吃瓜17", url: "https://xttkax.cn/" },
            mainEntityOfPage: { "@type": "WebPage", "@id": `https://xttkax.cn/news/${article.id}` },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "今日首页", item: "https://xttkax.cn/" },
              { "@type": "ListItem", position: 2, name: "资讯速递", item: "https://xttkax.cn/news" },
              { "@type": "ListItem", position: 3, name: article.title },
            ],
          },
        ]
      : [],
  });

  if (!article) {
    return (
      <div className="pt-16 md:pt-[72px]">
        <div className="mx-auto flex max-w-[1200px] flex-col items-center px-4 py-24 text-center">
          <i className="ri-error-warning-line text-5xl text-primary-500" aria-hidden="true"></i>
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">内容不存在或已被移除</h1>
          <p className="mt-2 text-foreground-600">这条资讯可能已下架，去资讯速递看看其他新鲜内容吧。</p>
          <Link to="/news" className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600">
            <i className="ri-arrow-left-line" aria-hidden="true"></i>返回资讯速递
          </Link>
        </div>
      </div>
    );
  }

  const cat = getCategory(article.category);
  const related = ARTICLES.filter((a) => a.id !== article.id && a.category === article.category).slice(0, 3);
  const fallback = ARTICLES.filter((a) => a.id !== article.id).filter((a) => !related.some((r) => r.id === a.id)).slice(0, 3 - related.length);

  return (
    <div className="pt-16 md:pt-[72px]">
      <div className="mx-auto max-w-[820px] px-4 py-10 md:px-6 md:py-12">
        <Breadcrumb
          items={[
            { label: "今日首页", to: "/" },
            { label: "资讯速递", to: "/news" },
            { label: cat?.name ?? "资讯" },
            { label: article.title },
          ]}
        />

        <header className="mt-6">
          <div className="flex flex-wrap items-center gap-2">
            {cat && (
              <Link to={`/news?cat=${article.category}`} className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700 hover:bg-primary-200">
                <i className={cat.icon} aria-hidden="true"></i>{cat.name}
              </Link>
            )}
            {article.tags.map((t) => (
              <span key={t} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-foreground-600">{t}</span>
            ))}
          </div>
          <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-[32px]">{article.title}</h1>
          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-y border-background-200/80 py-4 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary-200 text-xs font-semibold text-foreground-700">{article.author.slice(0, 1)}</span>
              {article.author}
            </span>
            <span className="flex items-center gap-1"><i className="ri-time-line" aria-hidden="true"></i>{article.date}</span>
            <span className="flex items-center gap-1"><i className="ri-eye-line" aria-hidden="true"></i>{formatCount(article.views)} 阅读</span>
            <span className="flex items-center gap-1"><i className="ri-time-fill" aria-hidden="true"></i>{article.readMinutes} 分钟阅读</span>
          </div>
        </header>

        <img src={article.cover} alt={article.coverAlt} className="mt-6 aspect-[16/9] w-full rounded-2xl object-cover" />

        <div className="mt-6 rounded-2xl border-l-4 border-primary-500 bg-primary-100/60 px-5 py-4 text-base font-medium leading-relaxed text-foreground-800 md:text-lg">
          {article.lead}
        </div>

        <article className="mt-6 space-y-5 text-base leading-8 text-foreground-700 md:text-[17px]">
          {article.paragraphs.map((p, i) => (
            <p key={i} className="first-letter:font-heading first-letter:text-2xl first-letter:font-bold first-letter:text-primary-600">{p}</p>
          ))}
        </article>

        <div className="mt-8 flex flex-wrap gap-2 border-t border-background-200/80 pt-6">
          {article.tags.map((t) => (
            <span key={t} className="rounded-full bg-secondary-100 px-3.5 py-1.5 text-xs text-foreground-700"># {t}</span>
          ))}
        </div>

        <div className="mt-8 flex items-center justify-between rounded-2xl bg-secondary-100 px-5 py-4">
          <p className="text-sm text-foreground-600">内容由 {article.author} 整理分享 · 理性吃瓜，尊重真实</p>
          <Link to="/news" className="inline-flex shrink-0 items-center gap-1 text-sm font-semibold text-primary-700 hover:underline">
            更多速递 <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </div>
      </div>

      <section className="border-t border-background-200/70 bg-background-100 py-12">
        <div className="mx-auto max-w-[1200px] px-4 md:px-6">
          <h2 className="mb-6 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            <i className="ri-file-copy-line text-primary-500" aria-hidden="true"></i>相关推荐
          </h2>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {[...related, ...fallback].map((a) => <ArticleCard key={a.id} article={a} />)}
          </div>
        </div>
      </section>
    </div>
  );
}